import React, { useMemo, useState } from 'react';
import { ScaleIcon, SearchIcon } from 'lucide-react';
import { balances } from '../../data/timeOff';
import { useHRStore } from '../../hooks/useHRStore';
import type { Navigate } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { policyName, scheduledDays, usedYtd } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { FilterSelect } from '../ui/FilterSelect';
export function BalancesTable({
  onNavigate
}: {
  onNavigate: Navigate;
}) {
  const {
    employees,
    policies
  } = useHRStore();
  const [query, setQuery] = useState('');
  const [policy, setPolicy] = useState('All');
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return balances.map(b => {
      const employee = employees.find(e => e.id === b.employeeId);
      return employee ? {
        ...b,
        employee,
        used: usedYtd(employee),
        scheduled: scheduledDays(b.employeeId)
      } : null;
    }).filter((b): b is NonNullable<typeof b> => Boolean(b)).filter(b => (policy === 'All' || policyName(b.policyId) === policy) && (!q || displayName(b.employee).toLowerCase().includes(q))).sort((a, b) => a.employee.lastName.localeCompare(b.employee.lastName));
  }, [query, policy, employees]);
  return <Card title="Balances by employee" subtitle={`${rows.length} enrolled · as of Nov 4, 2026 · in days`} icon={ScaleIcon} bodyClassName="pb-2" action={<div className="hidden gap-2 md:flex" data-id="element-584" data-mp="1sn8mzk3iz2ys">
          <div className="relative" data-id="element-585" data-mp="1smfm2zr8z7ok">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-586" data-mp="1xe74gn09273m" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Find employee" aria-label="Find employee" className="h-10 w-48 rounded-xl border-0 bg-canvas pl-9 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-587" data-mp="zybfmc0uqqkt" />
          </div>
          <FilterSelect label="Policy" allLabel="All policies" value={policy} options={policies.map(p => p.name)} onChange={setPolicy} className="w-44" data-id="element-588" data-mp="2h382shiw7z98" />
        </div>} data-id="element-583" data-mp="1jo3mi88ekzjz">
      {rows.length === 0 ? <div className="px-6 pb-4" data-id="element-589" data-mp="1kb4ijiylqxft">
          <EmptyState icon={SearchIcon} title="No balances match" compact data-id="element-590" data-mp="1kc662mwt1cef" />
        </div> : <div className="overflow-x-auto" data-id="element-591" data-mp="1ybyrc2pkb0f0">
          <table className="w-full min-w-[820px] text-left text-sm" data-id="element-592" data-mp="1y8t30porc2kc">
            <thead data-id="element-593" data-mp="1tb18rp0n44cz">
              <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-594" data-mp="1tdxa7nbp5b66">
                <th scope="col" className="px-6 py-2.5" data-id="element-595" data-mp="34r82w823h20h">Employee</th>
                <th scope="col" className="px-6 py-2.5" data-id="element-596" data-mp="34t2cwrbtv9ps">Policy</th>
                <th scope="col" className="px-6 py-2.5 text-right" data-id="element-597" data-mp="3d7746c9i86qp">Available</th>
                <th scope="col" className="px-6 py-2.5 text-right" data-id="element-598" data-mp="3h9tycjtne3r2">Used YTD</th>
                <th scope="col" className="px-6 py-2.5 text-right" data-id="element-599" data-mp="33a2fo5s6rz6q">Scheduled</th>
                <th scope="col" className="px-6 py-2.5 text-right" data-id="element-600" data-mp="33c5f1gf8b4o4">Projected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-601" data-mp="3vyq7kk7fxmt9">
              {rows.map(b => <tr key={b.employeeId} onClick={() => onNavigate({
            page: 'profile',
            employeeId: b.employee.id,
            tab: 'timeoff'
          })} className="cursor-pointer transition-colors hover:bg-azure/[0.04]" data-id="element-602" data-mp="4rx5zf8qjlo7">
                  <td className="px-6 py-3" data-id="element-603" data-mp="3r7hvthin1pcx">
                    <div className="flex items-center gap-3" data-id="element-604" data-mp="3r6ggh0ulagpe">
                      <Avatar name={displayName(b.employee)} seed={b.employee.id} size="sm" data-id="element-605" data-mp="irg1n8kd14jm" />
                      <span data-id="element-606" data-mp="nieluj8gyms5">
                        <span className="block font-medium text-navy" data-id="element-607" data-mp="njpvv0sicqrp">{displayName(b.employee)}</span>
                        <span className="block text-xs text-muted" data-id="element-608" data-mp="2v30zo2hq8rvz">{b.employee.department}</span>
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3" data-id="element-609" data-mp="2m11ina6ppnt1">
                    <Badge tone={b.policyId === 'newhire' ? 'navy' : 'blue'} data-id="element-610" data-mp="2m1te8l0b7ljb">{policyName(b.policyId)}</Badge>
                  </td>
                  <td className="px-6 py-3 text-right text-base font-semibold tabular-nums text-navy" data-id="element-611" data-mp="3eq8pqxk8flpw">{b.available}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-slate-600" data-id="element-612" data-mp="39qm2rwelqr3w">{b.used}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-slate-600" data-id="element-613" data-mp="34zvw8fufwi7m">{b.scheduled}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-slate-600" data-id="element-614" data-mp="301udxx4u8mx7">{b.available - b.scheduled}</td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </Card>;
}